import React, { useMemo } from "react";
import { motion } from "framer-motion";

type Sparkle = {
  id: number;
  left: number;   // %
  top: number;    // %
  size: number;   // px
  delay: number;  // s
  duration: number; // s
};

type Zzz = {
  id: number;
  left: number;   // %
  top: number;    // %
  delay: number;  // s
  duration: number; // s
};

function rand(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

interface NestQAvatarProps {
  src?: string;
  size?: number;
}

export const NestQAvatar: React.FC<NestQAvatarProps> = ({
  src,
  size = 220,
}) => {
  // Determine which NestQ image to show based on time of day
  const getTimeBasedImage = useMemo(() => {
    if (src) return src; // Use provided src if specified
    
    const now = new Date();
    const hour = now.getHours();
    
    if (hour >= 6 && hour < 8) {
      // Early morning (6:00 - 7:59)
      return "https://sbuehpoxvuufciwzoqaq.supabase.co/storage/v1/object/public/app-images/NestQ_early_morning.png";
    } else if (hour >= 8 && hour < 9) {
      // Running time (8:00 - 8:59)
      return "https://sbuehpoxvuufciwzoqaq.supabase.co/storage/v1/object/public/app-images/NestQ_running.png";
    } else if (hour >= 9 && hour < 18) {
      // Daytime (9:00 - 17:59)
      return "https://sbuehpoxvuufciwzoqaq.supabase.co/storage/v1/object/public/app-images/NestQ_day.png";
    } else if (hour >= 18 && hour < 19) {
      // Teeth brushing time (18:00 - 18:59)
      return "https://sbuehpoxvuufciwzoqaq.supabase.co/storage/v1/object/public/app-images/NestQ_washing_teeth.png";
    } else {
      // Nighttime/Bedtime (19:00 - 5:59)
      return "https://sbuehpoxvuufciwzoqaq.supabase.co/storage/v1/object/public/app-images/NestQ_asleep.png";
    }
  }, [src]);

  // Check if NestQ is sleeping (to hide sparkles)
  const isSleeping = useMemo(() => {
    if (src) return false; // If custom src, assume not sleeping
    
    const now = new Date();
    const hour = now.getHours();
    
    return hour >= 19 || hour < 6; // 19:00 - 5:59
  }, [src]);

  const sparkles = useMemo<Sparkle[]>(() => {
    // Only show sparkles if NestQ is not sleeping
    if (isSleeping) return [];
    
    // sparkles positioned in a halo around the character, not on it
    return Array.from({ length: 6 }).map((_, i) => {
      const angle = (Math.PI * 2 * i) / 6 + rand(-0.3, 0.3); // Evenly distributed angles with randomness
      const distance = rand(45, 65); // Distance from center (45-65% of container size)
      
      return {
        id: i,
        left: 50 + Math.cos(angle) * distance, // Calculate x position from angle
        top: 50 + Math.sin(angle) * distance,  // Calculate y position from angle
        size: rand(3, 8),
        delay: rand(0, 5),
        duration: rand(2.5, 4.5),
      };
    });
  }, [isSleeping]);

  const zzzs = useMemo<Zzz[]>(() => {
    // Only show ZZZ if NestQ is sleeping
    if (!isSleeping) return [];
    
    // Create 3 ZZZ particles that float upward
    return Array.from({ length: 3 }).map((_, i) => ({
      id: i,
      left: rand(60, 85),  // Position on the right side of character
      top: rand(20, 40),   // Start near the head area
      delay: rand(0, 3),   // Staggered timing
      duration: rand(4, 6), // Slow floating up
    }));
  }, [isSleeping]);

  return (
    <div
      style={{
        width: size,
        height: size,
        position: "relative",
        display: "grid",
        placeItems: "center",
      }}
      aria-label="NestQ character"
    >
      {/* Character */}
      <motion.div
        style={{
          width: "100%",
          height: "100%",
          display: "grid",
          placeItems: "center",
        }}
        animate={{
          y: isSleeping ? [0, -1, 0] : [0, -6, 0],           // Less movement when sleeping
          scale: isSleeping ? [1, 1.005, 1] : [1, 1.02, 1],     // Less breathing when sleeping
          rotate: isSleeping ? [0, -2, 1, -1.5, 0.5, 0] : [0, 0, 0, 0, 0, 0], // Seamless rocking when sleeping
        }}
        transition={{
          duration: isSleeping ? 12 : 3.2,  // Much slower for sleeping (includes rotation)
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <motion.img
          src={getTimeBasedImage}
          alt="NestQ"
          draggable={false}
          style={{
            width: "92%",
            height: "92%",
            objectFit: "contain",
            userSelect: "none",
          }}
          animate={{
            filter: isSleeping ? [
              "drop-shadow(0 0 8px rgba(255, 220, 140, 0.15))",
              "drop-shadow(0 0 12px rgba(255, 220, 140, 0.3))",
              "drop-shadow(0 0 8px rgba(255, 220, 140, 0.15))",
            ] : [
              "drop-shadow(0 0 10px rgba(255, 220, 140, 0.25))",
              "drop-shadow(0 0 18px rgba(255, 220, 140, 0.55))",
              "drop-shadow(0 0 10px rgba(255, 220, 140, 0.25))",
            ],
          }}
          transition={{
            duration: isSleeping ? 6 : 2.6,  // Slower glow when sleeping
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>

      {/* Sparkles layer - now on top */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 10 }}>
        {sparkles.map((s) => (
          <motion.div
            key={s.id}
            style={{
              position: "absolute",
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: s.size,
              height: s.size,
              borderRadius: 999,
              background: "rgba(255, 233, 140, 0.95)",
              filter: "blur(0.2px)",
            }}
            animate={{
              opacity: [0, 1, 0.8, 0],    // Longer fade with plateau
              scale: [0.6, 1.2, 1.0, 0.4], // More gradual scale change
              y: [0, -6, -8, -4],        // Extended movement
            }}
            transition={{
              duration: s.duration,
              delay: s.delay,
              repeat: Infinity,
              repeatDelay: rand(0.5, 2), // Random delay between repeats
              ease: "easeInOut",
              times: [0, 0.2, 0.7, 1],  // Custom timing for longer fade
            }}
          />
        ))}
      </div>

      {/* ZZZ layer - only when sleeping */}
      {isSleeping && (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 15 }}>
          {zzzs.map((z) => (
            <motion.div
              key={z.id}
              style={{
                position: "absolute",
                left: `${z.left}%`,
                top: `${z.top}%`,
                fontSize: `${14 + z.id * 2}px`,
                fontWeight: "bold",
                color: "rgba(147, 197, 253, 0.8)", // Light blue color
                textShadow: "0 0 4px rgba(147, 197, 253, 0.4)",
                fontFamily: "monospace",
              }}
            >
              Z
              <motion.span
                animate={{
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                z
              </motion.span>
              <motion.span
                animate={{
                  opacity: [0.3, 0.8, 0.3],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                z
              </motion.span>
            </motion.div>
          ))}
          {zzzs.map((z) => (
            <motion.div
              key={`float-${z.id}`}
              style={{
                position: "absolute",
                left: `${z.left}%`,
                top: `${z.top}%`,
                fontSize: `${14 + z.id * 2}px`,
                fontWeight: "bold",
                color: "rgba(147, 197, 253, 0.6)", // Light blue color
                textShadow: "0 0 4px rgba(147, 197, 253, 0.4)",
                fontFamily: "monospace",
              }}
              animate={{
                opacity: [0, 1, 1, 0],
                y: [0, -15, -25, -35],
                x: [0, 5, 8, 10],
              }}
              transition={{
                duration: z.duration,
                delay: z.delay,
                repeat: Infinity,
                repeatDelay: rand(2, 4),
                ease: "easeOut",
              }}
            >
              zzz
            </motion.div>
          ))}
        </div>
      )}

      {/* Optional: "blink-ish" micro squash (cheat) */}
      {!isSleeping && (
        <motion.div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
          }}
          animate={{
            // more noticeable blink effect every few seconds
            scaleY: [1, 1, 0.85, 0.92, 1, 1],
            scaleX: [1, 1, 1.08, 0.98, 1, 1],
          }}
          transition={{
            duration: 4.5,  // More frequent blinks
            times: [0, 0.6, 0.62, 0.64, 0.66, 1], // Longer blink duration
            repeat: Infinity,
            ease: ["easeInOut", "easeInOut", "easeIn", "easeOut", "easeInOut", "easeInOut"],
          }}
        />
      )}
    </div>
  );
};
